import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { useGroupedCables } from '../../hooks/useGroupedCables';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useGroupTitleManagement } from '../../hooks/useGroupTitleManagement';
import { handleFileImport, exportToXLSX } from '../../utils/fileOperations';
import { getCableDisplayName, getGroupedCablesAsArray, formatSezione, getTerminalData, analyzeAndGroupCables, getEarthCableDisplayName } from '../../utils/cableLogic';
import { createSortContext, universalSort } from '../../utils/sortingLogic';
import { getGroupTitle, updateGroupTitle } from '../../utils/groups';
import { MacroTessera } from './MacroTessera';
import { NestedMacroTessera } from './NestedMacroTessera';
import CableTessera from './CableTessera';
import { Icons } from '../icons/Icons';
import NestedCableList from './UnifiedCablesListNested';
import { buildNestedCableStructure } from '../../utils/nestedCableStructure';

const canJoinAdjacentSheathRun = (currentRun, cable) => {
  if (!currentRun || currentRun.length === 0) return false;

  const previousCable = currentRun[currentRun.length - 1];
  return previousCable.guaina === cable.guaina &&
    cable.originalIndex === previousCable.originalIndex + 1;
};

const getStableGroupKey = (group, fallbackPrefix = 'group') => {
  const indexes = (group?.cables || [])
    .map(cable => cable?.originalIndex)
    .filter(index => index !== undefined && index !== null)
    .sort((a, b) => a - b)
    .join('-');

  return indexes ? `${fallbackPrefix}-${indexes}` : `${fallbackPrefix}-empty`;
};


export const UnifiedCablesList = ({
  cables,
  setCables,
  setWarnings,
  editCable,
  editMultipolarCable,
  deleteCable,
  deleteMultipolarCable,
  settings,
  setScrollToCable,
  hoveredCable,
  setHoveredCable,
  editingIndex,
  editingMultipolarIndex,
  // New setters for file import
  setSettings,
  setRevision,
  setSchemaElettrico,
  setInstallazione,
  setFileName,
  setGroupTitles,
  setSuccessMessage, // Add setSuccessMessage here
  setListSortMode,
  // Selection props
  isSelectionModeEnabled = false,
  setIsSelectionModeEnabled = () => { },
  selectedCables = new Set(),
  toggleCableSelection = () => { },
  selectAllCables = () => { },
  deselectAllCables = () => { },
  // Terminali for export
  terminali = {},
  // Sheaths
  availableSheaths = [],
  setAvailableSheaths,
  validateCable,
  cableCatalog = { unipolari: [], multipolari: [] }
}) => {
  const { t } = useI18n();
  // const [listSortMode, setListSortMode] = useState('numerazione'); // Rimosso: ora viene gestito tramite props e settings
  const [exportForEW3035, setExportForEW3035] = useState(false);
  const [showDeleteButtons, setShowDeleteButtons] = useState(new Set());
  const useNestedView = settings.listSortMode === 'numerazione';

  const toggleDeleteButton = useCallback((originalIndex) => {
    // Blocca toggling durante editing
    if (editingIndex >= 0 || editingMultipolarIndex >= 0) return;
    setShowDeleteButtons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(originalIndex)) {
        newSet.delete(originalIndex);
      } else {
        newSet.add(originalIndex);
      }
      return newSet;
    });
  }, [editingIndex, editingMultipolarIndex]);

  // Pulisci sempre lo stato delete quando cambia lo stato di editing
  useEffect(() => {
    setShowDeleteButtons(new Set());
  }, [editingIndex, editingMultipolarIndex]);

  const handleEditCableWrapped = useCallback((index) => {
    setShowDeleteButtons(new Set());
    editCable(index);
  }, [editCable]);

  const handleEditMultipolarCableWrapped = useCallback((index) => {
    setShowDeleteButtons(new Set());
    editMultipolarCable(index);
  }, [editMultipolarCable]);

  const reorderCables = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    const newCables = [...cables];
    const [draggedCable] = newCables.splice(fromIndex, 1);
    newCables.splice(toIndex, 0, draggedCable);

    const updatedCables = newCables.map((cable, index) => ({
      ...cable,
      originalIndex: index
    }));

    setCables(updatedCables);
  }, [cables, setCables]);

  const { draggedIndex, dragOverIndex, handleDragStart, handleDragEnd, handleDragOver, handleDragEnter, handleDragLeave, handleDrop } = useDragAndDrop(settings.isDragModeEnabled, reorderCables);

  const getGroupTitleLocal = useCallback((groupName) => {
    return getGroupTitle(groupName, settings.groupTitles);
  }, [settings.groupTitles]);

  const updateGroupTitleLocal = useCallback((groupName, newTitle) => {
    updateGroupTitle(groupName, newTitle, setGroupTitles);
  }, [setGroupTitles]);

  const { getCurrentGroupTitle, handleGroupTitleChange, handleGroupTitleBlur, handleGroupTitleKeyDown, editingGroupTitle } = useGroupTitleManagement(getGroupTitleLocal, updateGroupTitleLocal);

  // Funzione per eliminare i cavi selezionati
  const deleteSelectedCables = useCallback(() => {
    if (selectedCables.size === 0) return;

    const cablesToDelete = Array.from(selectedCables);
    const filteredCables = cables.filter(cable => !selectedCables.has(cable.originalIndex));

    const updatedCables = filteredCables.map((cable, newIndex) => ({
      ...cable,
      originalIndex: newIndex
    }));

    setCables(updatedCables);
    deselectAllCables();
    setIsSelectionModeEnabled(false);

    setSuccessMessage(t('fileOperations.cablesDeletedSuccess', { count: cablesToDelete.length }));
    setTimeout(() => setSuccessMessage(''), 3000);
  }, [selectedCables, cables, setCables, deselectAllCables, setIsSelectionModeEnabled, setSuccessMessage, t]);

  const { groupCablesByMode } = useGroupedCables(cables, settings.listSortMode, settings);

  const groupSortedCablesForList = useCallback((cablesArray) => {
    const sortMode = settings.listSortMode;

    // ✅ MODALITÀ COLORE, SEZIONE, COLORE+SEZIONE: Non raggruppare, solo mantenere l'ordine
    if (sortMode === 'colore' || sortMode === 'sezione' || sortMode === 'coloreSezione') {
      const standard = [];
      const multipolar = [];
      cablesArray.forEach((cable, idx) => {
        const group = {
          type: cable.type || 'single',
          cables: [cable],
          key: `single-${cable.originalIndex || idx}`
        };
        if (cable.poli && Array.isArray(cable.poli)) {
          multipolar.push(group);
        } else {
          standard.push(group);
        }
      });
      return [...standard, ...multipolar];
    }

    // ✅ STEP 0: Se i cavi non hanno il tipo assegnato, applica analyzeAndGroupCables
    const standardCables = cablesArray.filter(cable => !(cable.poli && Array.isArray(cable.poli)));
    const needsAnalysis = standardCables.some(cable => cable.type !== 'single' && cable.type !== 'multiple');

    if (needsAnalysis) {
      const subGroups = analyzeAndGroupCables(standardCables, settings);
      // Assegna il tipo ai cavi
      subGroups.forEach(group => {
        group.cables.forEach(cable => {
          cable.type = group.isSingle ? 'single' : 'multiple';
        });
      });
    }

    // ✅ STEP 1: PRIORITÀ CAVI MULTIPLI - Applica logica multipli a TUTTI i cavi
    const groups = [];
    let currentGroup = null;

    cablesArray.forEach((cable, index) => {
      // Cavi multipolari: trattali come gruppi singoli individuali
      if (cable.poli && Array.isArray(cable.poli)) {
        if (currentGroup) {
          groups.push(currentGroup);
          currentGroup = null;
        }
        groups.push({
          type: 'single',
          cables: [cable],
          key: `multipolar-${cable.originalIndex}`
        });
        return;
      }

      if (cable.type === 'single') {
        if (currentGroup) {
          groups.push(currentGroup);
        }

        currentGroup = {
          type: 'single',
          cables: [cable],
          key: `single-${cable.originalIndex}`
        };
        groups.push(currentGroup);
        currentGroup = null;

      } else if (cable.type === 'multiple') {
        if (!currentGroup) {
          currentGroup = {
            type: 'multiple',
            cables: [cable],
            key: `multi-${cable.originalIndex}`
          };
        } else {
          const canJoin = currentGroup.type === 'multiple' && currentGroup.cables.some(existingCable => {
            const shareConnection =
              (cable.collegamentoA &&
                (cable.collegamentoA === existingCable.collegamentoA ||
                  cable.collegamentoA === existingCable.collegamentoB)) ||
              (cable.collegamentoB &&
                (cable.collegamentoB === existingCable.collegamentoA ||
                  cable.collegamentoB === existingCable.collegamentoB));

            // ✅ Controlla anche la siglatura: se non siamo in modalità collegamento,
            // i cavi devono avere la stessa siglatura per essere raggruppati
            const sameSiglatura = settings.marcaturePerCollegamento ||
              (cable.siglatura === existingCable.siglatura);

            if (shareConnection && cable.colore !== existingCable.colore) {
              const indexDifference = Math.abs(cable.originalIndex - existingCable.originalIndex);
              return indexDifference === 1 && sameSiglatura;
            }
            return shareConnection && sameSiglatura;
          });

          if (canJoin) {
            currentGroup.cables.push(cable);
          } else {
            groups.push(currentGroup);
            currentGroup = {
              type: 'multiple',
              cables: [cable],
              key: `multi-${cable.originalIndex}`
            };
          }
        }
      }
    });

    if (currentGroup) {
      groups.push(currentGroup);
    }

    const groupedByAdjacentSheathRuns = [];
    let currentSheathRun = null;

    const flushCurrentSheathRun = () => {
      if (!currentSheathRun || currentSheathRun.length === 0) return;

      groupedByAdjacentSheathRuns.push({
        type: 'multiple',
        cables: currentSheathRun,
        key: `sheath-${currentSheathRun[0].guaina}-${currentSheathRun[0].originalIndex}`
      });
      currentSheathRun = null;
    };

    groups.forEach(group => {
      const cable = group.cables[0];
      const isSingleSheathCable = group.type === 'single' && cable?.guaina;
      const shouldGroupBySheath = (sortMode === 'numerazione' || sortMode === 'gruppo') && isSingleSheathCable;

      if (shouldGroupBySheath) {
        if (canJoinAdjacentSheathRun(currentSheathRun, cable)) {
          currentSheathRun.push(cable);
        } else {
          flushCurrentSheathRun();
          currentSheathRun = [cable];
        }
        return;
      }

      flushCurrentSheathRun();
      groupedByAdjacentSheathRuns.push(group);
    });

    flushCurrentSheathRun();

    if (sortMode === 'numerazione') {
      return groupedByAdjacentSheathRuns.sort((a, b) => {
        const firstIndexA = Math.min(...a.cables.map(c => c.originalIndex));
        const firstIndexB = Math.min(...b.cables.map(c => c.originalIndex));
        return firstIndexA - firstIndexB;
      });
    }

    if (sortMode === 'siglatura') {
      return groupedByAdjacentSheathRuns.sort((a, b) => {
        const aIsMultipolar = a.cables[0]?.poli && Array.isArray(a.cables[0].poli);
        const bIsMultipolar = b.cables[0]?.poli && Array.isArray(b.cables[0].poli);
        if (aIsMultipolar && !bIsMultipolar) return 1;
        if (!aIsMultipolar && bIsMultipolar) return -1;
        const sigA = a.cables[0]?.siglatura || '';
        const sigB = b.cables[0]?.siglatura || '';
        const numA = parseInt(sigA);
        const numB = parseInt(sigB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return String(sigA).localeCompare(String(sigB));
      });
    }

    return groupedByAdjacentSheathRuns;
    /*

    // ✅ STEP 2: Raggruppa per guaina SOLO i cavi SINGOLI rimasti
    // MODALITÀ SIGLATURA: Non raggruppare per guaina, solo multipli
    const finalGroups = [];
    const singleCablesWithSheath = [];

    groups.forEach(group => {
      if (group.type === 'single' && group.cables[0].guaina) {
        // Cavo singolo con guaina
        if (sortMode === 'siglatura') {
          // Modalità siglatura: non raggruppare per guaina, lascia come singolo
          finalGroups.push(group);
        } else {
          // Modalità numerazione: accumula per raggruppamento successivo
          singleCablesWithSheath.push(group.cables[0]);
        }
      } else {
        // Gruppo multiplo o cavo singolo senza guaina - mantieni così
        finalGroups.push(group);
      }
    });

    // Raggruppa i cavi singoli con guaina per ID guaina (per modalità numerazione e gruppo)
    if ((sortMode === 'numerazione' || sortMode === 'gruppo') && singleCablesWithSheath.length > 0) {
      const sheathMap = {};
      singleCablesWithSheath.forEach(cable => {
        const sheathId = cable.guaina;
        if (!sheathMap[sheathId]) {
          sheathMap[sheathId] = [];
        }
        sheathMap[sheathId].push(cable);
      });

      // Ordina le guaine per indice del primo cavo
      const sheathGroups = Object.entries(sheathMap)
        .map(([sheathId, cables]) => ({
          sheathId,
          cables,
          firstIndex: Math.min(...cables.map(c => c.originalIndex))
        }))
        .sort((a, b) => a.firstIndex - b.firstIndex);

      sheathGroups.forEach(({ cables }) => {
        finalGroups.push({
          type: 'multiple',
          cables: cables,
          key: `sheath-${cables[0].guaina}-${cables[0].originalIndex}`
        });
      });
    }

    // ✅ STEP 3: Riordina tutti i gruppi
    if (sortMode === 'numerazione') {
      // Modalità numerazione: ordina per originalIndex
      return finalGroups.sort((a, b) => {
        const firstIndexA = Math.min(...a.cables.map(c => c.originalIndex));
        const firstIndexB = Math.min(...b.cables.map(c => c.originalIndex));
        return firstIndexA - firstIndexB;
      });
    }

    if (sortMode === 'siglatura') {
      // Modalità siglatura: ordina per siglatura, multipolari sempre in fondo
      return finalGroups.sort((a, b) => {
        const aIsMultipolar = a.cables[0]?.poli && Array.isArray(a.cables[0].poli);
        const bIsMultipolar = b.cables[0]?.poli && Array.isArray(b.cables[0].poli);
        // Multipolari sempre in fondo
        if (aIsMultipolar && !bIsMultipolar) return 1;
        if (!aIsMultipolar && bIsMultipolar) return -1;
        // Tra cavi dello stesso tipo, ordina per siglatura
        const sigA = a.cables[0]?.siglatura || '';
        const sigB = b.cables[0]?.siglatura || '';
        const numA = parseInt(sigA);
        const numB = parseInt(sigB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return String(sigA).localeCompare(String(sigB));
      });
    }

    // Per altre modalità, mantieni l'ordine originale
    return finalGroups;
    */
  }, [settings]);

  // Aggiorna processCablesForList per includere groupSortedCablesForList nelle dipendenze
  const processCablesForListMemo = useCallback((cablesArray) => {
    // Per modalità numerazione: ordina per originalIndex
    // Per altre modalità: mantieni l'ordine originale (già ordinato da useGroupedCables)
    const cablesInOrder = settings.listSortMode === 'numerazione'
      ? [...cablesArray].sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0))
      : [...cablesArray];

    // Applica analyzeAndGroupCables per assegnare i tipi
    const subGroups = analyzeAndGroupCables(cablesInOrder, settings);

    // Assegna il tipo ai cavi
    subGroups.forEach(subGroup => {
      subGroup.cables.forEach(cable => {
        cable.type = subGroup.isSingle ? 'single' : 'multiple';
      });
    });

    // Applica groupSortedCablesForList per la priorità e raggruppamento finale
    return groupSortedCablesForList(cablesInOrder);
  }, [settings, groupSortedCablesForList]);

  const groupedCables = useMemo(() => groupCablesByMode(), [groupCablesByMode]);

  // Per modalità colore/sezione/colore+sezione: ordina i cavi direttamente senza raggruppamento
  const sortedAndGroupedCables = useMemo(() => {
    const skipGroupingModes = ['colore', 'sezione', 'coloreSezione'];
    if (skipGroupingModes.includes(settings.listSortMode)) {
      // Ordina i cavi direttamente senza raggruppamento
      const context = createSortContext(settings.lingua, settings.useAWG);
      const sortedCables = universalSort(
        cables.map((cable, idx) => ({
          ...cable,
          originalIndex: cable.originalIndex !== undefined ? cable.originalIndex : idx,
          siglatura: settings.marcaturePerCollegamento
            ? getCableDisplayName(cable, settings.lingua, settings)
            : getEarthCableDisplayName(cable, settings.lingua)
        })),
        settings.listSortMode,
        context
      );

      // Crea un gruppo per ogni cavo (nessun raggruppamento)
      return sortedCables.map((cable, idx) => ({
        key: `cable_${cable.originalIndex}`,
        cables: [cable],
        isForcedGroup: false,
        firstCable: cable
      }));
    }

    return getGroupedCablesAsArray(groupedCables, cables, settings.listSortMode, settings);
  }, [groupedCables, cables, settings]);

  // Quantità e riepiloghi

  // This function is needed for export to correctly sort cables before writing to sheets
  const getSortedCablesForList = useCallback((cablesToSort) => {
    const cablesWithIndex = cablesToSort.map((cable, idx) => ({
      ...cable,
      originalIndex: cable.originalIndex !== undefined ? cable.originalIndex : idx
    }));

    const context = createSortContext(settings.lingua, settings.useAWG);
    return universalSort(cablesWithIndex, settings.listSortMode, context);
  }, [settings.listSortMode, settings.lingua, settings.useAWG]);

  return (
    <div>
      {/* Header con conteggi unificati */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-semibold">
          {t('cableList.cableList')} ({cables.length})
        </h2>
        <div className="flex gap-2">
          <label className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 cursor-pointer">
            <Icons.Upload className="w-4 h-4" />
            <span className="hidden md:inline">{t('cableList.importXlsx')}</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => handleFileImport(
                e, setCables, setWarnings,
                setSettings, setRevision, setSchemaElettrico, setInstallazione, setFileName, setGroupTitles, setListSortMode, setSuccessMessage,
                setAvailableSheaths,
                cableCatalog
              )}
              className="hidden"
            />
          </label>
          <button
            onClick={() => exportToXLSX(
              cables, settings, isSelectionModeEnabled, selectedCables, setWarnings, setSuccessMessage,
              getSortedCablesForList, getGroupTitleLocal, getCableDisplayName, formatSezione, getTerminalData, exportForEW3035, terminali, availableSheaths
            )}
            disabled={cables.length === 0}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300 transition-colors flex items-center gap-2"
          >
            <Icons.Download className="w-4 h-4" />
            <span className="hidden md:inline">{t('cableList.exportXlsx')}</span>
          </button>
        </div>
      </div>

      {/* Riga controlli principale */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:gap-4 mb-4 p-3 bg-white rounded-lg border border-gray-200">
        {/* Selector ordinamento - sempre visibile */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">{t('cableList.sortOrder')}</label>
          <select
            value={settings.listSortMode}
            onChange={(e) => {
              setListSortMode(e.target.value);
              if (e.target.value !== 'numerazione') {
                settings.setIsDragModeEnabled(false);
              }
              if (e.target.value !== 'coloreSezione') {
                setExportForEW3035(false);
              }
            }}
            className="border rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
            title={t('cableList.selectSortType')}
          >
            <option value="numerazione">{t('cableList.sortByNumber')}</option>
            <option value="siglatura">{t('cableList.sortBySignature')}</option>
            <option value="colore">{t('cableList.sortByColor')}</option>
            <option value="sezione">{t('cableList.sortBySection')}</option>
            <option value="coloreSezione">{t('cableList.sortByColorSection')}</option>
            <option value="gruppo">{t('cableList.sortByGroup')}</option>
          </select>
        </div>

        {/* Toggles a destra */}
        <div className="flex items-center gap-4 md:pl-6 md:border-l md:border-gray-300">
          {/* Toggle selezione cavi - disabilitato se drag è attivo */}
          <div className="flex items-center gap-3 -ml-2">
            <span className={`text-sm font-medium ${settings.isDragModeEnabled ? 'text-gray-400' : 'text-gray-700'}`}>
              {t('cableList.selectCables')}
            </span>
            <label className={`flex items-center ${settings.isDragModeEnabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isSelectionModeEnabled}
                  onChange={(e) => {
                    if (!settings.isDragModeEnabled) {
                      setIsSelectionModeEnabled(e.target.checked);
                      if (!e.target.checked) {
                        deselectAllCables();
                      }
                    }
                  }}
                  disabled={settings.isDragModeEnabled}
                  className="sr-only"
                />
                <div className={`
                  w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                  ${settings.isDragModeEnabled
                    ? 'bg-gray-200 cursor-not-allowed'
                    : isSelectionModeEnabled
                      ? 'bg-green-600'
                      : 'bg-gray-300'
                  }
                `}>
                  <div className={`
                    w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                    ${isSelectionModeEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                  `} />
                </div>
              </div>
            </label>
          </div>

          {/* Separatore verticale tra Seleziona Cavi e Ordina Manualmente */}
          {settings.listSortMode === 'numerazione' && (
            <div className="h-6 w-px bg-gray-300"></div>
          )}

          {/* Toggle riordino manuale - disabilitato in modalità selezione */}
          {settings.listSortMode === 'numerazione' && (
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${isSelectionModeEnabled ? 'text-gray-400' : 'text-gray-700'}`}>
                {t('cableList.manualSort')}
              </span>
              <label className={`flex items-center ${isSelectionModeEnabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={settings.isDragModeEnabled}
                    onChange={(e) => {
                      if (!isSelectionModeEnabled) {
                        settings.setIsDragModeEnabled(e.target.checked);
                      }
                    }}
                    disabled={isSelectionModeEnabled}
                    className="sr-only"
                  />
                  <div className={`
                    w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                    ${isSelectionModeEnabled
                      ? 'bg-gray-200 cursor-not-allowed'
                      : settings.isDragModeEnabled
                        ? 'bg-blue-600'
                        : 'bg-gray-300'
                    }
                  `}>
                    <div className={`
                      w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                      ${settings.isDragModeEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                    `} />
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Separatore verticale tra Seleziona Cavi e Esporta per EW-3035+L */}
          {settings.listSortMode === 'coloreSezione' && (
            <div className="h-6 w-px bg-gray-300"></div>
          )}

          {settings.listSortMode === 'coloreSezione' && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                {t('cableList.exportForEW3035')}
              </span>
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={exportForEW3035}
                    onChange={(e) => setExportForEW3035(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`
                    w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                    ${exportForEW3035 ? 'bg-green-600' : 'bg-gray-300'}
                  `}>
                    <div className={`
                      w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                      ${exportForEW3035 ? 'translate-x-5' : 'translate-x-0.5'}
                    `} />
                  </div>
                </div>
              </label>
            </div>
          )}

        </div>
      </div>

      {/* Seconda barra accessoria - solo quando selezione è attiva */}
      {isSelectionModeEnabled && (
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:gap-0 mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <span className="text-sm font-medium text-green-800">
                {selectedCables.size}/{cables.length} {t('cableList.selected')}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Pulsanti seleziona/deseleziona tutto */}
            <div className="flex items-center gap-2">
              <button
                onClick={selectAllCables}
                className="px-3 py-1.5 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors font-medium"
              >
                {t('cableList.selectAll')}
              </button>
              <button
                onClick={deselectAllCables}
                className="px-3 py-1.5 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors font-medium"
              >
                {t('cableList.deselectAll')}
              </button>
              {/* Pulsante elimina con testo */}
              <button
                onClick={deleteSelectedCables}
                disabled={selectedCables.size === 0}
                className="px-3 py-1.5 text-sm text-red-700 bg-red-100 hover:bg-red-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed rounded-md transition-colors font-medium flex items-center gap-2"
                title={t('cableList.deleteSelectedTitle', { count: selectedCables.size })}
              >
                <Icons.Trash2 className="w-4 h-4" />
                {t('cableList.deleteSelected')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Lista cavi raggruppata */}
      <div className="h-[785px] overflow-y-auto pr-2 pt-1">
        {cables.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Icons.FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{t('cableList.noCablesInserted')}</p>
            <p className="text-xs mt-1">{t('cableList.addCablesToSeeSchema')}</p>
          </div>
        ) : (
          <div className="flex gap-4">
            {/* Elenco cavi */}
            <div className="flex-1 space-y-2">
              {(() => {
                // ✅ Raccogli tutti i gruppi standard (non forzati) insieme per applicare la priorità
                const forcedGroups = [];
                const normalGroups = [];

                sortedAndGroupedCables.forEach((group) => {
                  if (group.isForcedGroup) {
                    forcedGroups.push(group);
                  } else {
                    normalGroups.push(group);
                  }
                });

                // ✅ Unisci tutti i cavi dei gruppi normali insieme
                const allNormalCables = normalGroups.flatMap(group => group.cables);

                // ✅ Usa la funzione comune per processare i cavi (stessa logica dei gruppi forzati)
                if (useNestedView) {
                  const nestedNodes = buildNestedCableStructure({
                    settings,
                    forcedGroups,
                    normalCables: allNormalCables
                  });
                  return (
                    <NestedCableList
                      nodes={nestedNodes}
                      settings={settings}
                      onCableClick={(siglatura, originalIndex) => {
                        if (!settings.isDragModeEnabled && !isSelectionModeEnabled) {
                          setScrollToCable({ siglatura, originalIndex });
                          setHoveredCable(originalIndex);
                        }
                      }}
                      onEditCable={handleEditCableWrapped}
                      onEditMultipolarCable={handleEditMultipolarCableWrapped}
                      onDeleteCable={deleteCable}
                      onDeleteMultipolarCable={deleteMultipolarCable}
                      hoveredCable={hoveredCable}
                      setHoveredCable={setHoveredCable}
                      handleDragStart={handleDragStart}
                      handleDragEnd={handleDragEnd}
                      handleDragOver={handleDragOver}
                      handleDragEnter={handleDragEnter}
                      handleDragLeave={handleDragLeave}
                      handleDrop={handleDrop}
                      draggedIndex={draggedIndex}
                      dragOverIndex={dragOverIndex}
                      editingIndex={editingIndex}
                      editingMultipolarIndex={editingMultipolarIndex}
                      showDeleteButtons={showDeleteButtons}
                      onToggleDelete={toggleDeleteButton}
                      isSelectionModeEnabled={isSelectionModeEnabled}
                      selectedCables={selectedCables}
                      onToggleSelection={toggleCableSelection}
                      availableSheaths={availableSheaths}
                      allCables={cables}
                      getCurrentGroupTitle={getCurrentGroupTitle}
                      handleGroupTitleChange={handleGroupTitleChange}
                      handleGroupTitleBlur={handleGroupTitleBlur}
                      handleGroupTitleKeyDown={handleGroupTitleKeyDown}
                      validateCable={validateCable}
                    />
                  );
                }

                const sortedNormalGroups = processCablesForListMemo(allNormalCables);

                // ✅ Crea un array di gruppi da renderizzare: prima i forzati, poi i normali ordinati
                const groupsToRender = [
                  ...forcedGroups.map(group => ({ ...group, isForcedGroup: true })),
                  ...sortedNormalGroups.map((sortedGroup) => ({
                    key: sortedGroup.key || getStableGroupKey(sortedGroup, 'normal-sorted'),
                    cables: sortedGroup.cables,
                    isForcedGroup: false,
                    isNormalSorted: true,
                    sortedGroupData: sortedGroup
                  }))
                ];

                return groupsToRender.map((group) => {
                  if (group.isForcedGroup) {
                    return (
                      <NestedMacroTessera
                        key={group.key}
                        groupName={group.groupData.groupName}
                        groupData={group.groupData}
                        onCableClick={(siglatura, originalIndex) => {
                          if (!settings.isDragModeEnabled && !isSelectionModeEnabled) {
                            setScrollToCable({
                              siglatura: siglatura,
                              originalIndex: originalIndex
                            });
                            setHoveredCable(originalIndex);
                          }
                        }}
                        onEditCable={handleEditCableWrapped}
                        onEditMultipolarCable={handleEditMultipolarCableWrapped}
                        onDeleteCable={deleteCable}
                        onDeleteMultipolarCable={deleteMultipolarCable}
                        validateCable={(c) => validateCable(c, cables, settings.lingua, settings.useAWG, settings)}
                        settings={settings}
                        isDragEnabled={settings.isDragModeEnabled}
                        handleDragStart={handleDragStart}
                        handleDragEnd={handleDragEnd}
                        handleDragOver={handleDragOver}
                        handleDragEnter={handleDragEnter}
                        handleDragLeave={handleDragLeave}
                        handleDrop={handleDrop}
                        draggedIndex={draggedIndex}
                        dragOverIndex={dragOverIndex}
                        editingIndex={editingIndex}
                        editingMultipolarIndex={editingMultipolarIndex}
                        showDeleteButtons={showDeleteButtons}
                        onToggleDelete={toggleDeleteButton}
                        listSortMode={settings.listSortMode}
                        getCableDisplayName={getCableDisplayName}
                        getCurrentGroupTitle={getCurrentGroupTitle}
                        handleGroupTitleChange={handleGroupTitleChange}
                        handleGroupTitleBlur={handleGroupTitleBlur}
                        handleGroupTitleKeyDown={handleGroupTitleKeyDown}
                        editingGroupTitle={editingGroupTitle}
                        setScrollToCable={setScrollToCable}
                        hoveredCable={hoveredCable}
                        setHoveredCable={setHoveredCable}
                        isSelectionModeEnabled={isSelectionModeEnabled}
                        selectedCables={selectedCables}
                        onToggleSelection={toggleCableSelection}
                        groupSortedCablesForList={processCablesForListMemo}
                        availableSheaths={availableSheaths}
                      />
                    );
                  } else if (group.isNormalSorted) {
                    // ✅ Gruppo normale già processato con priorità
                    const sortedGroup = group.sortedGroupData;
                    const firstCable = sortedGroup.cables[0];
                    const displaySiglatura = getCableDisplayName(firstCable, settings.lingua, settings);

                    // Verifica se è un gruppo guaina
                    const isSheathGroup = sortedGroup.key && sortedGroup.key.startsWith('sheath-');
                    const sheathId = isSheathGroup ? parseInt(sortedGroup.key.replace('sheath-', '').split('-')[0]) : null;

                    if (sortedGroup.cables.length === 1) {
                      // Cavo singolo
                      const cable = sortedGroup.cables[0];
                      const validation = validateCable(cable, cables, settings.lingua, settings.useAWG, settings);
                      const hasIssues = !validation.isValid;
                      const isDragging = draggedIndex === cable.originalIndex;
                      const isDragOver = dragOverIndex === cable.originalIndex && draggedIndex !== cable.originalIndex;
                      const isHovered = hoveredCable === cable.originalIndex;

                      return (
                        <div
                          key={`single-${cable.originalIndex}`}
                          className={`relative`}
                          draggable={settings.isDragModeEnabled}
                          onDragStart={(e) => handleDragStart(e, cable.originalIndex)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, cable.originalIndex)}
                          onDragEnter={(e) => handleDragEnter(e, cable.originalIndex)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, cable.originalIndex)}
                          onMouseEnter={() => !settings.isDragModeEnabled && setHoveredCable(cable.originalIndex)}
                          onMouseLeave={() => !settings.isDragModeEnabled && setHoveredCable(null)}
                        >
                          {settings.isDragModeEnabled && (
                            <div className={`absolute left-4 text-gray-400 hover:text-blue-600 transition-colors z-10 ${cable.poli && Array.isArray(cable.poli)
                              ? 'top-7'
                              : 'top-1/2 transform -translate-y-1/2'
                              }`}>
                              <Icons.DragHandle className="w-4 h-4" />
                            </div>
                          )}

                          <CableTessera
                            cable={cable}
                            validateCable={(c) => validateCable(c, cables, settings.lingua, settings.useAWG, settings)}
                            displaySiglatura={displaySiglatura}
                            onCableClick={(siglatura, originalIndex) => {
                              if (!settings.isDragModeEnabled && !isSelectionModeEnabled) {
                                setScrollToCable({
                                  siglatura: siglatura,
                                  originalIndex: originalIndex
                                });
                                setHoveredCable(originalIndex);
                              }
                            }}
                            onEditCable={handleEditCableWrapped}
                            onEditMultipolarCable={handleEditMultipolarCableWrapped}
                            onDeleteCable={deleteCable}
                            onDeleteMultipolarCable={deleteMultipolarCable}
                            showSiglatura={true}
                            clickable={!settings.isDragModeEnabled && !isSelectionModeEnabled}
                            settings={settings}
                            isDragEnabled={settings.isDragModeEnabled}
                            isDragging={isDragging}
                            isDragOver={isDragOver}
                            isHovered={isHovered}
                            hasIssues={hasIssues}
                            editingIndex={editingIndex}
                            editingMultipolarIndex={editingMultipolarIndex}
                            showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
                            onToggleDelete={() => toggleDeleteButton(cable.originalIndex)}
                            isSelectionModeEnabled={isSelectionModeEnabled}
                            selectedCables={selectedCables}
                            onToggleSelection={toggleCableSelection}
                            availableSheaths={availableSheaths}
                          />
                        </div>
                      );
                    } else {
                      // Gruppo multiplo o guaina
                      return (
                        <MacroTessera
                          key={group.key}
                          siglatura={displaySiglatura}
                          groupData={{
                            cables: sortedGroup.cables,
                            isMultiple: sortedGroup.type === 'multiple',
                            isSheathGroup: isSheathGroup,
                            sheathId: sheathId
                          }}
                          onCableClick={(siglatura, originalIndex) => {
                            if (!settings.isDragModeEnabled && !isSelectionModeEnabled) {
                              setScrollToCable({
                                siglatura: siglatura,
                                originalIndex: originalIndex
                              });
                              setHoveredCable(originalIndex);
                            }
                          }}
                          onEditCable={handleEditCableWrapped}
                          onEditMultipolarCable={handleEditMultipolarCableWrapped}
                          onDeleteCable={deleteCable}
                          onDeleteMultipolarCable={deleteMultipolarCable}
                          validateCable={(c) => validateCable(c, cables, settings.lingua, settings.useAWG, settings)}
                          settings={settings}
                          isDragEnabled={settings.isDragModeEnabled}
                          displaySiglatura={displaySiglatura}
                          hoveredCable={hoveredCable}
                          setHoveredCable={setHoveredCable}
                          handleDragStart={handleDragStart}
                          handleDragEnd={handleDragEnd}
                          handleDragOver={handleDragOver}
                          handleDragEnter={handleDragEnter}
                          handleDragLeave={handleDragLeave}
                          handleDrop={handleDrop}
                          draggedIndex={draggedIndex}
                          dragOverIndex={dragOverIndex}
                          editingIndex={editingIndex}
                          editingMultipolarIndex={editingMultipolarIndex}
                          showDeleteButtons={showDeleteButtons}
                          onToggleDelete={toggleDeleteButton}
                          isSelectionModeEnabled={isSelectionModeEnabled}
                          selectedCables={selectedCables}
                          onToggleSelection={toggleCableSelection}
                          availableSheaths={availableSheaths}
                        />
                      );
                    }
                  } else if (group.cables.length === 1) {
                    // Fallback per cavi singoli (non dovrebbe più arrivare qui)
                    const cable = group.cables[0];
                    const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);
                     const validation = validateCable(cable, cables, settings.lingua, settings.useAWG, settings);
                     const hasIssues = !validation.isValid;
                     const isDragging = draggedIndex === cable.originalIndex;
                     const isDragOver = dragOverIndex === cable.originalIndex && draggedIndex !== cable.originalIndex;
                     const isHovered = hoveredCable === cable.originalIndex;

                     return (
                       <div
                         key={`single-${cable.originalIndex}`}
                        className={`relative`}
                        draggable={settings.isDragModeEnabled}
                         onDragStart={(e) => handleDragStart(e, cable.originalIndex)}
                         onDragEnd={handleDragEnd}
                         onDragOver={(e) => handleDragOver(e, cable.originalIndex)}
                         onDragEnter={(e) => handleDragEnter(e, cable.originalIndex)}
                         onDragLeave={handleDragLeave}
                         onDrop={(e) => handleDrop(e, cable.originalIndex)}
                         onMouseEnter={() => !settings.isDragModeEnabled && setHoveredCable(cable.originalIndex)}
                         onMouseLeave={() => !settings.isDragModeEnabled && setHoveredCable(null)}
                       >
                        {settings.isDragModeEnabled && (
                          <div className={`absolute left-4 text-gray-400 hover:text-blue-600 transition-colors z-10 ${cable.poli && Array.isArray(cable.poli)
                            ? 'top-7'
                            : 'top-1/2 transform -translate-y-1/2'
                            }`}>
                            <Icons.DragHandle className="w-4 h-4" />
                          </div>
                        )}

                        <CableTessera
                          cable={cable}
                          validateCable={(c) => validateCable(c, cables, settings.lingua, settings.useAWG, settings)}
                          displaySiglatura={displaySiglatura}
                          onCableClick={(siglatura, originalIndex) => {
                            if (!settings.isDragModeEnabled && !isSelectionModeEnabled) {
                              setScrollToCable({
                                siglatura: siglatura,
                                originalIndex: originalIndex
                              });
                              setHoveredCable(originalIndex);
                            }
                          }}
                          onEditCable={handleEditCableWrapped}
                          onEditMultipolarCable={handleEditMultipolarCableWrapped}
                          onDeleteCable={deleteCable}
                          onDeleteMultipolarCable={deleteMultipolarCable}
                          showSiglatura={true}
                          clickable={!settings.isDragModeEnabled && !isSelectionModeEnabled}
                           settings={settings}
                           isDragEnabled={settings.isDragModeEnabled}
                           isDragging={isDragging}
                           isDragOver={isDragOver}
                           isHovered={isHovered}
                           hasIssues={hasIssues}
                           editingIndex={editingIndex}
                           editingMultipolarIndex={editingMultipolarIndex}
                          showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
                          onToggleDelete={() => toggleDeleteButton(cable.originalIndex)}
                          isSelectionModeEnabled={isSelectionModeEnabled}
                          selectedCables={selectedCables}
                          onToggleSelection={toggleCableSelection}
                          availableSheaths={availableSheaths}
                        />
                      </div>
                    );
                  } else {
                    // Fallback per gruppi multipli (non dovrebbe più arrivare qui)
                    const firstCable = group.cables[0];
                    const displaySiglatura = getCableDisplayName(firstCable, settings.lingua, settings);

                    // Verifica se è un gruppo guaina
                    const isSheathGroup = group.key && group.key.startsWith('sheath_');
                    const sheathId = isSheathGroup ? parseInt(group.key.replace('sheath_', '')) : null;

                    return (
                      <MacroTessera
                        key={group.key}
                        siglatura={displaySiglatura}
                        groupData={{
                          cables: group.cables,
                          isMultiple: true,
                          isSheathGroup: isSheathGroup,
                          sheathId: sheathId
                        }}
                        onCableClick={(siglatura, originalIndex) => {
                          if (!settings.isDragModeEnabled && !isSelectionModeEnabled) {
                            setScrollToCable({
                              siglatura: siglatura,
                              originalIndex: originalIndex
                            });
                            setHoveredCable(originalIndex);
                          }
                        }}
                        onEditCable={handleEditCableWrapped}
                        onEditMultipolarCable={handleEditMultipolarCableWrapped}
                        onDeleteCable={deleteCable}
                        onDeleteMultipolarCable={deleteMultipolarCable}
                        validateCable={(c) => validateCable(c, cables, settings.lingua, settings.useAWG, settings)}
                        settings={settings}
                        isDragEnabled={settings.isDragModeEnabled}
                        displaySiglatura={displaySiglatura}
                        hoveredCable={hoveredCable}
                        setHoveredCable={setHoveredCable}
                        handleDragStart={handleDragStart}
                        handleDragEnd={handleDragEnd}
                        handleDragOver={handleDragOver}
                        handleDragEnter={handleDragEnter}
                        handleDragLeave={handleDragLeave}
                        handleDrop={handleDrop}
                        draggedIndex={draggedIndex}
                        dragOverIndex={dragOverIndex}
                        editingIndex={editingIndex}
                        editingMultipolarIndex={editingMultipolarIndex}
                        showDeleteButtons={showDeleteButtons}
                        onToggleDelete={toggleDeleteButton}
                        isSelectionModeEnabled={isSelectionModeEnabled}
                        selectedCables={selectedCables}
                        onToggleSelection={toggleCableSelection}
                        availableSheaths={availableSheaths}
                        getCurrentGroupTitle={getCurrentGroupTitle}
                        handleGroupTitleChange={handleGroupTitleChange}
                        handleGroupTitleBlur={handleGroupTitleBlur}
                        handleGroupTitleKeyDown={handleGroupTitleKeyDown}
                      />
                    );
                  }
                });
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
